export const LOCAL_STORAGE_KEYS = {
  TICKETING_SELECTED_BOARD: 'ticketing-selected-board',
  TICKETING_SELECTED_VIEW: 'ticketing-selected-view',
  TICKETING_REQUESTOR_PORTAL: 'ticketing-requestor-portal',
  TICKETING_AUDIT_PORTAL: 'ticketing-audit-portal',
  UNSAVED_VIEW_STORAGE_KEY_PREFIX: 'ticketing-view-unsaved',
  /* Shared config across boards */
  TICKETING_BOARD_CONFIG: 'ticketing-view-board-config',
  /* Ephemeral view state: client-side only, never saved to board view configuration */
  TICKETING_BOARD_VIEW_EPHEMERAL: 'ticketing-view-ephemeral',
  TICKETING_DETAIL_PANEL_WIDTH: 'ticketing-detail-panel-width',
  TICKETING_TASK_LIST_COLUMN_WIDTHS: 'ticketing-task-list-column-widths',

  TICKETING_EMAIL_INTAKE_LOGS: 'ticketing-email-intake-logs',
} as const;

type StorageKey = (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];

/** Limit of caching items in local storage. */
export const MAX_CACHE_LENGTH = 10;

export const removeRecord = (key: string | StorageKey) => {
  if (key) {
    localStorage.removeItem(key);
  } else {
    localStorage.clear();
  }
};

export const addRecord = (
  key: string | StorageKey,
  value: unknown,
  expiryInDays?: number
) => {
  localStorage.setItem(key, JSON.stringify(value));

  if (expiryInDays) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryInDays);
    localStorage.setItem(`${key}-expiryDate`, expiryDate.toString());
  }
};

export const getAllRecords = (key: string | StorageKey) => {
  let value = [];

  const expiryDateString = localStorage.getItem(`${key}-expiryDate`);
  if (expiryDateString) {
    const expiryDate = new Date(expiryDateString);
    if (new Date() >= expiryDate) {
      removeRecord(key);
      removeRecord(`${key}-expiryDate`);
    }
  }

  const data = localStorage.getItem(key);
  if (data) {
    value = JSON.parse(data) || [];
  }

  return value;
};

const setRecord = (
  key: string | StorageKey,
  value: Record<string, unknown>
) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const useLocalStorageManager = () => ({
  add: addRecord,
  remove: removeRecord,
  get: getAllRecords,
  set: setRecord,
});
